import { getCustomerState } from '@/context/customer/customer.utils'

import { DEFAULT_ONBOARDING_DATA, DEFAULT_PRODUCT_DATA } from './Onboarding.constants'
import { ApplianceFormValidationErrors } from './Onboarding.types'

import type { FORMIO_FIELDS } from './Onboarding.schema'
import type { OnboardingFormType, OnboardingState, OnboardingDataType, ApplianceFormValidationResult } from './Onboarding.types'
import type { OnboardingCookie } from '@/types/cookies'
import type { Customer } from '@/types/shopify/customer-account'

export const getInitialState = ({ customer, onboardingCookie }: { customer?: Customer | null; onboardingCookie?: OnboardingCookie }): OnboardingDataType => {
  if (customer) {
    const customerState = getCustomerState(customer)
    const customerB2BOnboardingInformation = customer?.b2bInformation?.jsonValue as OnboardingFormType | null

    if (customerState === 'ONBOARDED') {
      return {
        ...DEFAULT_ONBOARDING_DATA,
        ...DEFAULT_PRODUCT_DATA,
        customerState,
        onboardingForm: {
          ...DEFAULT_ONBOARDING_DATA.onboardingForm,
        },
      }
    }

    if (customerState === 'B2B_ONBOARDING' && customerB2BOnboardingInformation) {
      return {
        ...DEFAULT_ONBOARDING_DATA,
        ...DEFAULT_PRODUCT_DATA,
        customerState,
        onboardingForm: customerB2BOnboardingInformation,
      }
    }
  }

  if (onboardingCookie) {
    return {
      ...DEFAULT_ONBOARDING_DATA,
      ...DEFAULT_PRODUCT_DATA,
      customerState: 'ONBOARDING' as OnboardingState,
      onboardingForm: {
        ...DEFAULT_ONBOARDING_DATA.onboardingForm,
        address: onboardingCookie.address,
      },
      displayAddress: onboardingCookie.displayAddress,
      productForm: {
        branchCode: onboardingCookie.branchCode,
        pricesByQuantity: onboardingCookie.product.pricesByQuantity,
        pricingStatus: onboardingCookie.product.pricingStatus,
        quantity: onboardingCookie.product.quantity,
        appliances: onboardingCookie.appliances || {},
      },
      initialAppliances: onboardingCookie.appliances || {},
    }
  }

  return {
    ...DEFAULT_ONBOARDING_DATA,
    ...DEFAULT_PRODUCT_DATA,
  }
}

export const getApplianceFormIsValid = (formData: FORMIO_FIELDS): ApplianceFormValidationResult => {
  if (!formData.hotWater && !formData.cooking && !formData.heating && !formData.other)
    return {
      isValid: false,
      error: ApplianceFormValidationErrors.NO_APPLIANCE_SELECTED,
    }

  if (formData.other && !formData.otherInput)
    return {
      isValid: false,
      error: ApplianceFormValidationErrors.OTHER_INPUT_INVALID,
    }

  const MAX_INPUT_LENGTH = 250
  const otherInput = formData.otherInput as string | undefined

  // If 'other' is selected, check if otherInput is empty or exceeds max length
  const inputLength = otherInput?.length ?? 0
  const isValid = inputLength > 0 && inputLength <= MAX_INPUT_LENGTH
  if (formData.other && !isValid)
    return {
      isValid: false,
      error: ApplianceFormValidationErrors.OTHER_INPUT_INVALID,
    }

  return {
    isValid: true,
    error: null,
  }
}
